import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { AutocompleteCDS, DataFieldCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { PlusCircleIcon } from '@sharedCR/coreDesignSystem/icons';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import AutomaticFeeChangeTag from 'app/shared/components/AutomaticFeeChangeTag';
import { getAutomaticFeeChangeActionType } from 'app/shared/components/AutomaticFeeChangeTag/AutomaticFeeChangeTag.utils';
import { CancelIcon, DeleteIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import FeeInfoIcon from 'app/shared/componentsRestyled/loanApplication/RecordingFeesData/FeeInfoIcon';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import { recordingTypeOptions } from 'constants/feeTypes';
import { FieldInputType } from 'constants/fields';
import { FeeCreditSourceLabel, getCreditTargetBucket } from 'constants/loanFee';
import { PAGE_CODES } from 'constants/pageCodes';
import { PendingChangesActionType } from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import { getConditionalArray } from 'utils/data';
import {
  getFilteredLoanFeesOptions,
  getIsFeeAmountEditingDisallowed,
  isFieldValueChanged,
} from 'utils/loanFees';
import { formatNumberToCurrencyString } from 'utils/number';

import { getMasterPermissionsToPage, selectIsClaimAllowed } from 'store/auth/selectors';
import { deleteLoanFeeCreditAction, getLoanFeeCreditsAction } from 'store/loanApplications/actions';
import {
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
  selectIsSellerPaidFieldsVisible,
  selectLoanFeeCreditsByFeeTypeId,
} from 'store/loanApplications/selectors';
import { selectFormHiddenPage } from 'store/loanApplications/selectors/lockedFields.selectors';
import {
  selectFeeProviderOptions,
  selectFeeTypes,
  selectFeeTypesOptionsWithoutSellerPaid,
} from 'store/system/selectors';

import { LoanFeesTestIds } from '../../../../../../LoanFees.testIds';
import { getAppraisalSurchargeTooltipContent } from '../../../AccordionDetailsView/FeeTable/FeeTable.utils';
import { useOpenApplyCreditModal } from '../../../ApplyCreditModal/hooks/useOpenApplyCreditModal';
import AprIndicator from '../../../AprIndicator';
import { ADD_NEW_FEE_PROVIDER } from '../FeeTable.constants';
import { feeProviderChange, feeTypeNameChange, getExtraFeeProps } from '../FeeTable.utils';

import FeeEditMenu from './FeeEditMenu';
import {
  applyCreditIconButtonStyles,
  applyCreditTooltipPopperProps,
  creditListStyles,
  creditRowStyles,
  deleteCreditButtonStyles,
  deleteCreditIconStyles,
  feeNameCellStyles,
  feeNameInputFieldWrapperStyles,
  feeNameInputRowStyles,
} from './FeeTableRow.styles';

const FeeTableRow = ({
  isLoading,
  vldErrors,
  index,
  handleChange,
  stringifiedConfigFormFees,
  leSection,
  validateField,
  setInForm,
  setIsTouched,
  setForm,
  isNonBorrowerPaidFieldsIncluded,
  isFeeProviderColumnVisible,
  validateForm,
  isFinancedAmountColumnVisible,
  onDeleteFee,
  prefix,
  pendingCredits,
  addPendingCredit,
  removePendingCredit,
  ...fee
}) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const isManageCicHistoryEnabled = useFeatureToggle(FeatureFlag.EnableManageCicHistory);

  const isSellerPaidFieldsVisible = useSelector(selectIsSellerPaidFieldsVisible);
  const feeTypes = useSelector(selectFeeTypes);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.FEES));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.FEES));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.FEES));
  const isPageHidden = useSelector(selectFormHiddenPage(FormNames.FEES));
  const creditsByFeeTypeId = useSelector(selectLoanFeeCreditsByFeeTypeId);

  const feesOptions = useSelector(selectFeeTypesOptionsWithoutSellerPaid);
  const feeProviderOptions = useSelector(selectFeeProviderOptions);

  const isEditFeeTypeNameAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_EDITOR_EDIT_FEE_TYPE_NAME),
  );
  const isEditFeeProviderNameAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_EDITOR_EDIT_FEE_PROVIDER_NAME),
  );
  const isManageDiscountPointsAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_EDITOR_MANAGE_DISCOUNT_POINTS),
  );
  const isOverrideSubProviderAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_FEES_OVERRIDE_SUB_PROVIDER),
  );

  const { isManageAllowed: isFeeProviderManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.FEE_PROVIDER),
  );

  const isDisabledByLock = isLoading || isPageLocked;

  const { value: feeTypeIdValue, countTowardsApr } = fee.feeTypeId || {};
  const isAmountEditingDisallowed = getIsFeeAmountEditingDisallowed(feeTypeIdValue, feeTypes);
  const savedCreditsRaw = creditsByFeeTypeId[feeTypeIdValue];

  const pendingCreditsForFee = useMemo(
    () => (pendingCredits || []).filter(credit => credit.feeTypeId === feeTypeIdValue),
    [pendingCredits, feeTypeIdValue],
  );

  const allCreditsForFee = useMemo(
    () => [
      ...(savedCreditsRaw || []).map(credit => ({
        ...credit,
        id: credit.loanFeeCreditId,
        isPending: false,
      })),
      ...pendingCreditsForFee.map(credit => ({ ...credit, isPending: true })),
    ],
    [savedCreditsRaw, pendingCreditsForFee],
  );

  const onDeleteSavedCredit = useCallback(
    e => {
      const feeCreditId = e.currentTarget.dataset.creditId;

      dispatch(deleteLoanFeeCreditAction({ loanId, feeCreditId })).then(() =>
        dispatch(getLoanFeeCreditsAction({ loanId })),
      );
    },
    [dispatch, loanId],
  );

  const onRemovePendingCreditClick = useCallback(
    e => {
      const { creditId } = e.currentTarget.dataset;
      const credit = pendingCreditsForFee.find(credit => credit.id === creditId);

      if (!credit) {
        return;
      }

      const targetBucket = getCreditTargetBucket(credit.creditSource);

      setForm(prev => {
        const next = _.cloneDeep(prev);
        const currentFee = next[prefix][index];

        _.set(
          next,
          `${prefix}.[${index}].borrowerPaidAtClosingAmount`,
          String(Number(currentFee.borrowerPaidAtClosingAmount || 0) + credit.amount),
        );
        _.set(
          next,
          `${prefix}.[${index}].${targetBucket}`,
          String(Number(currentFee[targetBucket] || 0) - credit.amount),
        );

        return next;
      });

      removePendingCredit(creditId);
    },
    [pendingCreditsForFee, prefix, index, setForm, removePendingCredit],
  );

  const onApplyCredit = useCallback(
    ({ amount, creditSource }) => {
      const creditAmount = Number(amount);
      const targetBucket = getCreditTargetBucket(creditSource);

      setForm(prev => {
        const next = _.cloneDeep(prev);
        const currentFee = next[prefix][index];

        _.set(
          next,
          `${prefix}.[${index}].borrowerPaidAtClosingAmount`,
          String(Number(currentFee.borrowerPaidAtClosingAmount || 0) - creditAmount),
        );
        _.set(
          next,
          `${prefix}.[${index}].${targetBucket}`,
          String(Number(currentFee[targetBucket] || 0) + creditAmount),
        );

        return next;
      });

      setIsTouched(true);

      addPendingCredit({
        id: nanoid(),
        feeTypeId: feeTypeIdValue,
        amount: creditAmount,
        creditSource,
        isPending: true,
      });
    },
    [feeTypeIdValue, prefix, index, setForm, setIsTouched, addPendingCredit],
  );

  const creditFormContext = useForm({ creditSource: null, amount: '' }, {});

  const openApplyCreditModal = useOpenApplyCreditModal({
    formContext: creditFormContext,
    onApplyCredit,
  });

  const onApplyCreditIconClick = useCallback(
    e => {
      e.stopPropagation();

      openApplyCreditModal();
    },
    [openApplyCreditModal],
  );

  const feeTypeNamePath = `${prefix}.[${index}].feeTypeName`;

  const isFinancedAmountDisabled =
    (fee.isLocallyAdded && !feeTypes[fee.feeTypeId?.value]?.includeIntoTotalLoanAmount) ||
    (!fee.isLocallyAdded && !fee.includeIntoTotalLoanAmount);

  const { isHardcodedFee, isDiscountPointsReadonly, onFilterOptions } = getExtraFeeProps({
    ...fee,
    isManageDiscountPointsAllowed,
  });

  const isAmountDisabled =
    isDiscountPointsReadonly || isDisabledByLock || isAmountEditingDisallowed;

  const { feeProviderCategoryId, feeProviderCategoryIds } = feeTypes[fee.feeTypeId?.value] || {};

  const primaryFeeProviderCategoryId = feeProviderCategoryIds?.[0] || feeProviderCategoryId || null;

  const hasMultipleCategories = (feeProviderCategoryIds?.length || 0) > 1;

  const isAddNewFeeProviderAllowed =
    !!primaryFeeProviderCategoryId && isFeeProviderManageAllowed && !hasMultipleCategories;

  const extendedFeeProviderOptions = useMemo(
    () => [
      ...getConditionalArray(isAddNewFeeProviderAllowed, [
        {
          name: 'Add New Fee Provider',
          value: ADD_NEW_FEE_PROVIDER,
          feeProviderCategoryId: primaryFeeProviderCategoryId,
        },
      ]),
      ...feeProviderOptions,
    ],
    [isAddNewFeeProviderAllowed, primaryFeeProviderCategoryId, feeProviderOptions],
  );

  const filteredLoanFeesOptions = useMemo(
    () =>
      getFilteredLoanFeesOptions({
        feesOptions,
        isManageDiscountPointsAllowed,
        stringifiedConfigFormFees,
        leSection,
      }),
    [feesOptions, stringifiedConfigFormFees, leSection, isManageDiscountPointsAllowed],
  );

  const onFeeTypeNameChange = useCallback(
    index => feeTypeNameChange({ index, setForm, setInForm, validateField, prefix }),
    [setForm, setInForm, validateField, prefix],
  );

  const onFeeProviderChange = useMemo(
    () =>
      feeProviderChange({
        index,
        dispatch,
        setInForm,
        validateField,
        prefix,
        setForm,
        setIsTouched,
        loanId,
        isOverrideSubProviderAllowed,
        feeTypeId: fee.feeTypeId,
        currentFeeProviderId: fee.feeProviderId?.value,
      }),
    [
      index,
      dispatch,
      setInForm,
      validateField,
      prefix,
      setForm,
      setIsTouched,
      loanId,
      isOverrideSubProviderAllowed,
      fee.feeTypeId,
      fee.feeProviderId?.value,
    ],
  );

  const onChangeAmountInput = useCallback(
    async (e, validationContext) => {
      await handleChange(e, validationContext);
      await validateForm();
    },
    [handleChange, validateForm],
  );

  const originalFeeTypeName = fee.feeTypeId?.name;

  if (fee.pendingChangesActionType === PendingChangesActionType.DELETED || isPageHidden) {
    return null;
  }

  return (
    <>
      <TableRow className={`table-row-pending-${fee.pendingChangesActionType}`}>
        {(!!fee.isWithRecordingType || !!fee.recordingType) && (
          <TableCell>
            <AutocompleteCDS
              disabled
              fullWidth
              withPopupError
              withTooltip
              dataTestId={LoanFeesTestIds.getFeeRecordingTypeSelectId(index)}
              error={!!_.get(vldErrors, `${prefix}.[${index}].recordingType`)}
              helperText={_.get(vldErrors, `${prefix}.[${index}].recordingType`)}
              id={`${prefix}.[${index}].recordingType`}
              isFieldChanged={isFieldValueChanged(fee, 'recordingType')}
              isHidden={hiddenFieldsMap.recordingType}
              name={`${prefix}.[${index}].recordingType`}
              options={recordingTypeOptions}
              placeholder="-"
              size="small"
              value={fee.recordingType}
              onChange={handleChange}
            />
          </TableCell>
        )}

        <TableCell>
          <Box sx={feeNameCellStyles}>
            {!fee.isLocallyAdded && (
              <>
                <Box sx={feeNameInputRowStyles}>
                  <Box sx={feeNameInputFieldWrapperStyles}>
                    <TextFieldCDS
                      fullWidth
                      withPopupError
                      dataTestId={LoanFeesTestIds.getFeeTypeNameInputId(index)}
                      disabled={
                        !isEditFeeTypeNameAllowed ||
                        isDiscountPointsReadonly ||
                        isHardcodedFee ||
                        isDisabledByLock ||
                        lockedFieldsMap.feeTypeName
                      }
                      error={!!_.get(vldErrors, feeTypeNamePath)}
                      helperText={_.get(vldErrors, feeTypeNamePath)}
                      id={feeTypeNamePath}
                      isFieldChanged={isFieldValueChanged(fee, 'feeTypeName')}
                      isHidden={hiddenFieldsMap.feeTypeName}
                      name={feeTypeNamePath}
                      placeholder="-"
                      size="small"
                      value={fee.feeTypeName}
                      onChange={handleChange}
                    />
                  </Box>
                  {!!feeTypeIdValue && (
                    <Tooltip
                      PopperProps={applyCreditTooltipPopperProps}
                      placement="top"
                      title="Apply Credit"
                    >
                      <span>
                        <IconButton
                          disableRipple
                          className="apply-credit-icon"
                          data-testid={LoanFeesTestIds.getFeeApplyCreditButtonId(index)}
                          disabled={isAmountDisabled || lockedFieldsMap.borrowerPaidAtClosingAmount}
                          size="small"
                          sx={applyCreditIconButtonStyles}
                          onClick={onApplyCreditIconClick}
                        >
                          <PlusCircleIcon size={IconSize[16]} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {!!fee.appraisalSurcharges?.length && (
                    <span data-testid={LoanFeesTestIds.APPRAISAL_SURCHARGE_INFO_ICON}>
                      <FeeInfoIcon
                        content={getAppraisalSurchargeTooltipContent(fee.appraisalSurcharges)}
                      />
                    </span>
                  )}
                </Box>

                {originalFeeTypeName && originalFeeTypeName !== fee.feeTypeName && (
                  <Typography color="text.secondary" variant="regular12">
                    ({originalFeeTypeName})
                  </Typography>
                )}
                <AprIndicator isIncludedInApr={countTowardsApr} />
                <AutomaticFeeChangeTag
                  pendingChangesActionType={getAutomaticFeeChangeActionType(fee.initialFee ?? fee)}
                />
                {!!allCreditsForFee.length && (
                  <Box sx={creditListStyles}>
                    {allCreditsForFee.map(credit => (
                      <Box key={credit.id} sx={creditRowStyles}>
                        <Typography color="text.secondary" variant="regular12">
                          Credit: {formatNumberToCurrencyString(credit.amount)} from{' '}
                          {FeeCreditSourceLabel[credit.creditSource]}
                        </Typography>
                        <IconButton
                          disableRipple
                          data-credit-id={credit.id}
                          data-testid={LoanFeesTestIds.getFeeDeleteCreditButtonId(credit.id)}
                          size="small"
                          sx={deleteCreditButtonStyles}
                          onClick={
                            credit.isPending ? onRemovePendingCreditClick : onDeleteSavedCredit
                          }
                        >
                          <CancelIcon style={deleteCreditIconStyles} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}

            {fee.isLocallyAdded && (
              <>
                <AutocompleteCDS
                  fullWidth
                  withPopupError
                  dataTestId={LoanFeesTestIds.getFeeTypeSelectId(index)}
                  disabled={
                    isDiscountPointsReadonly || isDisabledByLock || lockedFieldsMap.feeTypeName
                  }
                  error={!!_.get(vldErrors, `${prefix}.[${index}].feeTypeId`)}
                  helperText={_.get(vldErrors, `${prefix}.[${index}].feeTypeId`)}
                  id={`${prefix}.[${index}].feeTypeId`}
                  isFieldChanged={isFieldValueChanged(fee, 'feeTypeId')}
                  isHidden={hiddenFieldsMap.feeTypeId}
                  name={`${prefix}.[${index}].feeTypeId`}
                  options={filteredLoanFeesOptions}
                  placeholder="-"
                  size="small"
                  value={fee.feeTypeId}
                  onChange={onFeeTypeNameChange(index)}
                />
                <AprIndicator isIncludedInApr={countTowardsApr} />
              </>
            )}
          </Box>
        </TableCell>

        {isNonBorrowerPaidFieldsIncluded && isFeeProviderColumnVisible && (
          <TableCell>
            <AutocompleteCDS
              fullWidth
              withPopupError
              dataTestId={LoanFeesTestIds.getFeeProviderSelectId(index)}
              disabled={
                !isEditFeeProviderNameAllowed ||
                isDiscountPointsReadonly ||
                isDisabledByLock ||
                lockedFieldsMap.feeProviderId
              }
              error={!!_.get(vldErrors, `${prefix}.[${index}].feeProviderId`)}
              filterOptions={onFilterOptions}
              helperText={_.get(vldErrors, `${prefix}.[${index}].feeProviderId`)}
              id={`${prefix}.[${index}].feeProviderId`}
              isFieldChanged={isFieldValueChanged(fee, 'feeProviderId')}
              isHidden={hiddenFieldsMap.feeProviderId}
              name={`${prefix}.[${index}].feeProviderId`}
              options={extendedFeeProviderOptions}
              placeholder="-"
              size="small"
              value={fee.feeProviderId}
              onChange={onFeeProviderChange}
            />
          </TableCell>
        )}

        <TableCell align="center" colSpan={2}>
          <div>
            <span>
              <DataFieldCDS
                fullWidth
                withPopupError
                data-is-amount="true"
                disabled={isAmountDisabled || lockedFieldsMap.borrowerPaidAtClosingAmount}
                error={!!_.get(vldErrors, `${prefix}.[${index}].borrowerPaidAtClosingAmount`)}
                helperText={_.get(vldErrors, `${prefix}.[${index}].borrowerPaidAtClosingAmount`)}
                id={`${prefix}.[${index}].borrowerPaidAtClosingAmount`}
                isFieldChanged={isFieldValueChanged(fee, 'borrowerPaidAtClosingAmount')}
                isHidden={hiddenFieldsMap.borrowerPaidAtClosingAmount}
                name={`${prefix}.[${index}].borrowerPaidAtClosingAmount`}
                placeholder="-"
                size="small"
                type={FieldInputType.CURRENCY}
                value={fee.borrowerPaidAtClosingAmount}
                onChange={onChangeAmountInput}
              />
            </span>

            <span>
              <DataFieldCDS
                fullWidth
                withPopupError
                data-is-amount="true"
                disabled={isAmountDisabled || lockedFieldsMap.borrowerPaidBeforeClosingAmount}
                error={!!_.get(vldErrors, `${prefix}.[${index}].borrowerPaidBeforeClosingAmount`)}
                helperText={_.get(
                  vldErrors,
                  `${prefix}.[${index}].borrowerPaidBeforeClosingAmount`,
                )}
                id={`${prefix}.[${index}].borrowerPaidBeforeClosingAmount`}
                isFieldChanged={isFieldValueChanged(fee, 'borrowerPaidBeforeClosingAmount')}
                isHidden={hiddenFieldsMap.borrowerPaidBeforeClosingAmount}
                name={`${prefix}.[${index}].borrowerPaidBeforeClosingAmount`}
                placeholder="-"
                size="small"
                type={FieldInputType.CURRENCY}
                value={fee.borrowerPaidBeforeClosingAmount}
                onChange={onChangeAmountInput}
              />
            </span>
          </div>
        </TableCell>

        {isNonBorrowerPaidFieldsIncluded && (
          <>
            {isSellerPaidFieldsVisible && (
              <TableCell align="center" colSpan={2}>
                <div>
                  <span>
                    <DataFieldCDS
                      fullWidth
                      withPopupError
                      disabled={isAmountDisabled || lockedFieldsMap.sellerPaidAtClosingAmount}
                      error={!!_.get(vldErrors, `${prefix}.[${index}].sellerPaidAtClosingAmount`)}
                      helperText={_.get(
                        vldErrors,
                        `${prefix}.[${index}].sellerPaidAtClosingAmount`,
                      )}
                      id={`${prefix}.[${index}].sellerPaidAtClosingAmount`}
                      isFieldChanged={isFieldValueChanged(fee, 'sellerPaidAtClosingAmount')}
                      isHidden={hiddenFieldsMap.sellerPaidAtClosingAmount}
                      name={`${prefix}.[${index}].sellerPaidAtClosingAmount`}
                      placeholder="-"
                      size="small"
                      type={FieldInputType.CURRENCY}
                      value={fee.sellerPaidAtClosingAmount}
                      onChange={onChangeAmountInput}
                    />
                  </span>

                  <span>
                    <DataFieldCDS
                      fullWidth
                      withPopupError
                      disabled={isAmountDisabled || lockedFieldsMap.sellerPaidBeforeClosingAmount}
                      error={
                        !!_.get(vldErrors, `${prefix}.[${index}].sellerPaidBeforeClosingAmount`)
                      }
                      helperText={_.get(
                        vldErrors,
                        `${prefix}.[${index}].sellerPaidBeforeClosingAmount`,
                      )}
                      id={`${prefix}.[${index}].sellerPaidBeforeClosingAmount`}
                      isFieldChanged={isFieldValueChanged(fee, 'sellerPaidBeforeClosingAmount')}
                      isHidden={hiddenFieldsMap.sellerPaidBeforeClosingAmount}
                      name={`${prefix}.[${index}].sellerPaidBeforeClosingAmount`}
                      placeholder="-"
                      size="small"
                      type={FieldInputType.CURRENCY}
                      value={fee.sellerPaidBeforeClosingAmount}
                      onChange={onChangeAmountInput}
                    />
                  </span>
                </div>
              </TableCell>
            )}

            <TableCell align="center">
              <DataFieldCDS
                fullWidth
                withPopupError
                disabled={isAmountDisabled || lockedFieldsMap.paidByOthersAmount}
                error={!!_.get(vldErrors, `${prefix}.[${index}].paidByOthersAmount`)}
                helperText={_.get(vldErrors, `${prefix}.[${index}].paidByOthersAmount`)}
                id={`${prefix}.[${index}].paidByOthersAmount`}
                isFieldChanged={isFieldValueChanged(fee, 'paidByOthersAmount')}
                isHidden={hiddenFieldsMap.paidByOthersAmount}
                name={`${prefix}.[${index}].paidByOthersAmount`}
                placeholder="-"
                size="small"
                type={FieldInputType.CURRENCY}
                value={fee.paidByOthersAmount}
                onChange={onChangeAmountInput}
              />
            </TableCell>
          </>
        )}

        {isFinancedAmountColumnVisible && (
          <TableCell align="center" className="financed-amount-cell">
            <DataFieldCDS
              fullWidth
              withPopupError
              disabled={
                isAmountDisabled || isFinancedAmountDisabled || lockedFieldsMap.financedAmount
              }
              error={!!_.get(vldErrors, `${prefix}.[${index}].financedAmount`)}
              helperText={_.get(vldErrors, `${prefix}.[${index}].financedAmount`)}
              id={`${prefix}.[${index}].financedAmount`}
              isFieldChanged={isFieldValueChanged(fee, 'financedAmount')}
              isHidden={hiddenFieldsMap.financedAmount}
              name={`${prefix}.[${index}].financedAmount`}
              placeholder="-"
              size="small"
              type={FieldInputType.CURRENCY}
              value={fee.financedAmount}
              onChange={onChangeAmountInput}
            />
          </TableCell>
        )}

        <TableCell align="center">
          {!isManageCicHistoryEnabled && !!onDeleteFee && (
            <IconButton
              disableRipple
              disableTouchRipple
              className="label-button"
              data-index={index}
              data-testid={LoanFeesTestIds.getFeeDeleteButtonId(index)}
              disabled={isDiscountPointsReadonly || isLoading}
              onClick={onDeleteFee}
            >
              <DeleteIcon size={IconSize[20]} />
            </IconButton>
          )}
          {isManageCicHistoryEnabled && (
            <FeeEditMenu
              feeTypeId={fee.feeTypeId?.value}
              index={index}
              isDisabled={isLoading}
              isDisabledDelete={isDiscountPointsReadonly}
              onDeleteFee={onDeleteFee}
            />
          )}
        </TableCell>
      </TableRow>
    </>
  );
};

FeeTableRow.propTypes = {
  ...WithLoadingPropTypes,
  vldErrors: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  handleChange: PropTypes.func.isRequired,
  stringifiedConfigFormFees: PropTypes.string.isRequired,
  leSection: PropTypes.string.isRequired,
  validateField: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  setIsTouched: PropTypes.func.isRequired,
  setForm: PropTypes.func.isRequired,
  isNonBorrowerPaidFieldsIncluded: PropTypes.bool.isRequired,
  isFeeProviderColumnVisible: PropTypes.bool.isRequired,
  validateForm: PropTypes.func.isRequired,
  isFinancedAmountColumnVisible: PropTypes.bool.isRequired,
  onDeleteFee: PropTypes.func.isRequired,
  pendingCredits: PropTypes.array,
  addPendingCredit: PropTypes.func,
  removePendingCredit: PropTypes.func,
};

export default FeeTableRow;
